import React, {Component} from 'react';
import {Button, Col, Form, Nav, Row, Tab} from 'react-bootstrap';
import {connect} from 'react-redux';
import Sidebar from '../../components/common/sidebar';
import Loader from '../../components/common/Loader';
import {notify} from '../../components/common/Toster';
import {usersActions} from '../../_actions';
import {isValidEmail} from '../../utils/Validation';

const defaultProfileImage = require('../../assets/img/datedish logo .png');
const supportedImageTypes = ['image/jpeg', 'image/png', 'image/gif'];
const maximumProfileImageSize = 5000000;

class Settings extends Component {
    state = {
        activeType: 'Profile',
        isEdit: false,
        loader: true,
        name: '',
        email: '',
        profileImage: '',
        profilePic: null,
        profilePicPreview: '',
        firstNameErrorMsg: '',
        emailErrorMsg: '',
        profilePicErrorMsg: '',
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
        oldPasswordErrorMsg: '',
        newPasswordErrorMsg: '',
        confirmPasswordErrorMsg: '',
        errors: ''
    };

    componentDidMount() {
        this.getAdminInfo();
    }

    getErrorMessage = (source, fallbackMessage) => {
        if (!source) {
            return fallbackMessage;
        }

        const payload = source.response && source.response.data
            ? source.response.data
            : source.data || source;

        if (payload.error && payload.error.responseMessage) {
            return payload.error.responseMessage;
        }

        if (payload.responseMessage) {
            return payload.responseMessage;
        }

        return source.message || fallbackMessage;
    };

    getAdminInfo = () => {
        this.setState({loader: true, errors: ''});

        this.props.dispatch(usersActions.getAdminDetail({}))
            .then(data => {
                if (data.statusCode === 1) {
                    const userInfo = data.responseData || {};
                    const profileImage = userInfo.profileCover || '';

                    this.setState({
                        loader: false,
                        name: userInfo.name || '',
                        email: userInfo.email || '',
                        profileImage,
                        profilePic: null,
                        profilePicPreview: profileImage
                    });
                    return;
                }

                const message = this.getErrorMessage(data, 'Unable to load admin profile.');
                this.setState({loader: false, errors: message});
                notify('error', message);
            })
            .catch(error => {
                const message = this.getErrorMessage(error, 'Unable to load admin profile.');
                this.setState({loader: false, errors: message});
                notify('error', message);
            });
    };

    handleChanged = event => {
        const {name, value} = event.target;
        const errorFieldByInput = {
            name: 'firstNameErrorMsg',
            email: 'emailErrorMsg',
            oldPassword: 'oldPasswordErrorMsg',
            newPassword: 'newPasswordErrorMsg',
            confirmPassword: 'confirmPasswordErrorMsg'
        };

        this.setState({
            [name]: value,
            [errorFieldByInput[name]]: '',
            errors: ''
        });
    };

    editMode = () => {
        this.setState({
            isEdit: true,
            firstNameErrorMsg: '',
            emailErrorMsg: '',
            profilePicErrorMsg: '',
            errors: ''
        });
    };

    cancelEdit = () => {
        this.setState({isEdit: false}, this.getAdminInfo);
    };

    handleProfileValidation = () => {
        const {name, email, profilePic} = this.state;
        const normalizedEmail = email.trim().toLowerCase();
        let firstNameErrorMsg = '';
        let emailErrorMsg = '';
        let profilePicErrorMsg = '';

        if (!name.trim()) {
            firstNameErrorMsg = 'Please enter a name.';
        }

        if (!normalizedEmail) {
            emailErrorMsg = 'Please enter an email address.';
        } else if (!isValidEmail(normalizedEmail)) {
            emailErrorMsg = 'Please enter a valid email address.';
        }

        if (profilePic && profilePic.size > maximumProfileImageSize) {
            profilePicErrorMsg = 'Profile image size must be less than 5 MB.';
        }

        this.setState({firstNameErrorMsg, emailErrorMsg, profilePicErrorMsg});
        return !firstNameErrorMsg && !emailErrorMsg && !profilePicErrorMsg;
    };

    handlePicUpload = event => {
        const file = event.target.files && event.target.files[0];

        if (!file) {
            return;
        }

        if (supportedImageTypes.indexOf(file.type) === -1) {
            this.setState({profilePicErrorMsg: 'Please select a JPG, PNG, or GIF image.'});
            return;
        }

        if (file.size > maximumProfileImageSize) {
            this.setState({profilePicErrorMsg: 'Profile image size must be less than 5 MB.'});
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            this.setState({
                profilePic: file,
                profilePicPreview: reader.result,
                profilePicErrorMsg: ''
            });
        };
        reader.readAsDataURL(file);
    };

    handleEditSubmit = event => {
        event.preventDefault();

        if (!this.handleProfileValidation()) {
            return;
        }

        const {name, email, profilePic, profilePicPreview} = this.state;
        const formData = new FormData();
        formData.append('name', name.trim());
        formData.append('email', email.trim().toLowerCase());

        if (profilePic) {
            formData.append('profileCover', profilePic);
        }

        this.setState({loader: true, errors: ''});

        this.props.dispatch(usersActions.editProfile(formData))
            .then(data => {
                if (data.statusCode === 1) {
                    const responseData = data.responseData || {};
                    const updatedProfile = responseData.userProfile || {};
                    const profileImage = updatedProfile.profileCover || profilePicPreview;
                    const message = responseData.message || 'Profile updated successfully.';

                    this.setState({
                        isEdit: false,
                        profileImage,
                        profilePic: null,
                        profilePicPreview: profileImage
                    }, () => {
                        notify('success', message);
                        this.getAdminInfo();
                    });
                    return;
                }

                const message = this.getErrorMessage(data, 'Unable to update profile.');
                this.setState({loader: false, errors: message});
                notify('error', message);
            })
            .catch(error => {
                const message = this.getErrorMessage(error, 'Unable to update profile.');
                this.setState({loader: false, errors: message});
                notify('error', message);
            });
    };

    handlePasswordValidation = () => {
        const {oldPassword, newPassword, confirmPassword} = this.state;
        let oldPasswordErrorMsg = '';
        let newPasswordErrorMsg = '';
        let confirmPasswordErrorMsg = '';

        if (!oldPassword) {
            oldPasswordErrorMsg = 'Current password is required.';
        }

        if (!newPassword) {
            newPasswordErrorMsg = 'New password is required.';
        } else if (oldPassword === newPassword) {
            newPasswordErrorMsg = 'New password must be different from the current password.';
        }

        if (!confirmPassword) {
            confirmPasswordErrorMsg = 'Please confirm the new password.';
        } else if (confirmPassword !== newPassword) {
            confirmPasswordErrorMsg = 'New password and confirmation do not match.';
        }

        this.setState({
            oldPasswordErrorMsg,
            newPasswordErrorMsg,
            confirmPasswordErrorMsg
        });

        return !oldPasswordErrorMsg && !newPasswordErrorMsg && !confirmPasswordErrorMsg;
    };

    changePassword = event => {
        event.preventDefault();

        if (!this.handlePasswordValidation()) {
            return;
        }

        const {newPassword, oldPassword} = this.state;
        this.setState({loader: true, errors: ''});

        this.props.dispatch(usersActions.changePassword({newPassword, oldPassword}))
            .then(data => {
                if (data.statusCode === 1) {
                    this.setState({
                        loader: false,
                        activeType: 'Profile',
                        oldPassword: '',
                        newPassword: '',
                        confirmPassword: '',
                        oldPasswordErrorMsg: '',
                        newPasswordErrorMsg: '',
                        confirmPasswordErrorMsg: '',
                        errors: ''
                    }, () => {
                        notify('success', 'Password updated successfully.');
                        this.getAdminInfo();
                    });
                    return;
                }

                const message = this.getErrorMessage(data, 'Unable to update password.');
                this.setState({loader: false, errors: message});
                notify('error', message);
            })
            .catch(error => {
                const message = this.getErrorMessage(error, 'Unable to update password.');
                this.setState({loader: false, errors: message});
                notify('error', message);
            });
    };

    handleTabSelect = activeType => {
        if (activeType === 'Profile') {
            this.setState({
                activeType,
                isEdit: false,
                oldPassword: '',
                newPassword: '',
                confirmPassword: '',
                oldPasswordErrorMsg: '',
                newPasswordErrorMsg: '',
                confirmPasswordErrorMsg: '',
                errors: ''
            }, this.getAdminInfo);
            return;
        }

        this.setState({
            activeType: 'changepassword',
            isEdit: false,
            oldPassword: '',
            newPassword: '',
            confirmPassword: '',
            oldPasswordErrorMsg: '',
            newPasswordErrorMsg: '',
            confirmPasswordErrorMsg: '',
            errors: ''
        });
    };

    renderProfileImage() {
        const {profilePicPreview} = this.state;

        return (
            <img
                className="settings-avatar-image"
                src={profilePicPreview || defaultProfileImage}
                alt="Admin profile"
            />
        );
    }

    renderProfilePane() {
        const {
            isEdit,
            name,
            email,
            firstNameErrorMsg,
            emailErrorMsg,
            profilePicErrorMsg,
            errors
        } = this.state;

        return (
            <Tab.Pane eventKey="Profile">
                <div className="settings-section-header">
                    <div>
                        <h2>Admin profile</h2>
                    </div>
                    {!isEdit && (
                        <Button type="button" variant="info" onClick={this.editMode}>
                            <i className="fa fa-pencil" aria-hidden="true"/> Edit profile
                        </Button>
                    )}
                </div>

                {errors && <div className="error-message" role="alert">{errors}</div>}

                {isEdit ? (
                    <Form className="settings-form" onSubmit={this.handleEditSubmit} noValidate>
                        <div className="settings-avatar-editor">
                            <div className="settings-avatar-wrap">
                                {this.renderProfileImage()}
                                <label className="settings-avatar-upload" title="Choose profile image">
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,image/gif"
                                        onChange={this.handlePicUpload}
                                    />
                                    <i className="fa fa-camera" aria-hidden="true"/>
                                    <span className="sr-only">Choose profile image</span>
                                </label>
                            </div>
                            <div>
                                <strong>Profile image</strong>
                                <span>JPG, PNG, or GIF. Maximum size 5 MB.</span>
                            </div>
                        </div>
                        {profilePicErrorMsg && (
                            <div className="settings-field-error settings-avatar-error">{profilePicErrorMsg}</div>
                        )}

                        <Form.Group as={Row} controlId="adminName">
                            <Form.Label column md={4}>Name</Form.Label>
                            <Col md={8}>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={name}
                                    onChange={this.handleChanged}
                                    placeholder="Admin name"
                                    autoComplete="name"
                                />
                                {firstNameErrorMsg && <div className="settings-field-error">{firstNameErrorMsg}</div>}
                            </Col>
                        </Form.Group>

                        <Form.Group as={Row} controlId="adminEmail">
                            <Form.Label column md={4}>Email address</Form.Label>
                            <Col md={8}>
                                <Form.Control
                                    type="email"
                                    name="email"
                                    value={email}
                                    onChange={this.handleChanged}
                                    placeholder="admin@example.com"
                                    autoComplete="email"
                                />
                                <Form.Text className="text-muted">
                                    This email is used to sign in to the admin portal.
                                </Form.Text>
                                {emailErrorMsg && <div className="settings-field-error">{emailErrorMsg}</div>}
                            </Col>
                        </Form.Group>

                        <div className="settings-form-actions">
                            <Button type="button" variant="light" onClick={this.cancelEdit}>Cancel</Button>
                            <Button type="submit" variant="info">Save changes</Button>
                        </div>
                    </Form>
                ) : (
                    <div className="settings-profile-view">
                        <div className="settings-profile-summary">
                            {this.renderProfileImage()}
                            <div>
                                <h3>{name || 'Admin'}</h3>
                                <p>{email || 'No email address'}</p>
                            </div>
                        </div>
                        <div className="settings-detail-grid">
                            <div className="settings-detail-item">
                                <span>Name</span>
                                <strong>{name || '—'}</strong>
                            </div>
                            <div className="settings-detail-item">
                                <span>Email address</span>
                                <strong>{email || '—'}</strong>
                            </div>
                        </div>
                    </div>
                )}
            </Tab.Pane>
        );
    }

    renderPasswordPane() {
        const {
            oldPassword,
            newPassword,
            confirmPassword,
            oldPasswordErrorMsg,
            newPasswordErrorMsg,
            confirmPasswordErrorMsg,
            errors
        } = this.state;

        return (
            <Tab.Pane eventKey="changepassword">
                <div className="settings-section-header">
                    <div>
                        <h2>Change password</h2>
                        <p>Use a strong password that you do not use on other accounts.</p>
                    </div>
                    <div className="settings-security-icon" aria-hidden="true">
                        <i className="fa fa-lock"/>
                    </div>
                </div>

                {errors && <div className="error-message" role="alert">{errors}</div>}

                <Form className="settings-form" onSubmit={this.changePassword} noValidate>
                    <Form.Group as={Row} controlId="currentPassword">
                        <Form.Label column md={4}>Current password</Form.Label>
                        <Col md={8}>
                            <Form.Control
                                type="password"
                                name="oldPassword"
                                value={oldPassword}
                                onChange={this.handleChanged}
                                maxLength="30"
                                autoComplete="current-password"
                                placeholder="Enter current password"
                            />
                            {oldPasswordErrorMsg && <div className="settings-field-error">{oldPasswordErrorMsg}</div>}
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} controlId="newPassword">
                        <Form.Label column md={4}>New password</Form.Label>
                        <Col md={8}>
                            <Form.Control
                                type="password"
                                name="newPassword"
                                value={newPassword}
                                onChange={this.handleChanged}
                                maxLength="30"
                                autoComplete="new-password"
                                placeholder="Enter new password"
                            />
                            {newPasswordErrorMsg && <div className="settings-field-error">{newPasswordErrorMsg}</div>}
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} controlId="confirmPassword">
                        <Form.Label column md={4}>Confirm new password</Form.Label>
                        <Col md={8}>
                            <Form.Control
                                type="password"
                                name="confirmPassword"
                                value={confirmPassword}
                                onChange={this.handleChanged}
                                maxLength="30"
                                autoComplete="new-password"
                                placeholder="Re-enter new password"
                            />
                            {confirmPasswordErrorMsg && (
                                <div className="settings-field-error">{confirmPasswordErrorMsg}</div>
                            )}
                        </Col>
                    </Form.Group>

                    <div className="settings-form-actions">
                        <Button type="button" variant="light" onClick={() => this.handleTabSelect('Profile')}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="info">Update password</Button>
                    </div>
                </Form>
            </Tab.Pane>
        );
    }

    render() {
        const {activeType, loader, profileImage} = this.state;

        return (
            <>
                <Sidebar profilePicPreview={profileImage}/>
                <div className="body_container">
                    <div className="body_inner_wrapper">
                        {loader ? (
                            <Loader/>
                        ) : (
                            <>
                                <header className="header_content settings-page-header">
                                    <div>
                                        <p className="settings-page-title">Settings</p>
                                        <span>Manage your admin account and security.</span>
                                    </div>
                                </header>
                                <div className="main_content settings-main-content">
                                    <div className="setting_wrapper settings-card">
                                        <Tab.Container
                                            activeKey={activeType}
                                            onSelect={this.handleTabSelect}
                                        >
                                            <Row noGutters className="settings-layout">
                                                <Col md={4} lg={3} className="settings-nav-column">
                                                    <div className="settings-nav-heading">Account</div>
                                                    <Nav variant="pills" className="flex-column settings-nav">
                                                        <Nav.Item>
                                                            <Nav.Link eventKey="Profile">
                                                                <span className="settings-nav-icon">
                                                                    <i className="fa fa-user" aria-hidden="true"/>
                                                                </span>
                                                                <span className="settings-nav-copy">
                                                                    <strong>Profile</strong>
                                                                    <small>Name, email, and photo</small>
                                                                </span>
                                                            </Nav.Link>
                                                        </Nav.Item>
                                                        <Nav.Item>
                                                            <Nav.Link eventKey="changepassword">
                                                                <span className="settings-nav-icon">
                                                                    <i className="fa fa-lock" aria-hidden="true"/>
                                                                </span>
                                                                <span className="settings-nav-copy">
                                                                    <strong>Password</strong>
                                                                    <small>Update account security</small>
                                                                </span>
                                                            </Nav.Link>
                                                        </Nav.Item>
                                                    </Nav>
                                                </Col>
                                                <Col md={8} lg={9} className="settings-content-column">
                                                    <Tab.Content className="settings-tab-content">
                                                        {this.renderProfilePane()}
                                                        {this.renderPasswordPane()}
                                                    </Tab.Content>
                                                </Col>
                                            </Row>
                                        </Tab.Container>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </>
        );
    }
}

export default connect()(Settings);
